import classNames from 'clsx';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_WEBVIEW, IS_WEBVIEW_FROM_UA } from 'reactApp/appHelpers/configHelpers';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { promotionEndDate } from 'reactApp/appHelpers/featuresHelpers';
import { mayPromo2025YearProductId } from 'reactApp/appHelpers/featuresHelpers/features/mayPromo2025';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { revealPlatformPopup } from 'reactApp/sections/QuotaLanding/TariffsPlans/TariffPlansCard/TariffPlansCard.helpers';
import { Button } from 'reactApp/ui/Button/Button';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { sendTmrGoal, TMR3473039 } from 'reactApp/utils/tmr';

import styles from './PromoCounter.css';
import { usePromoCountdown } from './usePromoCountdown';

export function PromoCounter({
    isIOSPromo = false,
    isAndroidPromo = false,
    isMobilePayAvailable,
    mayPromo2025Enabled,
    isCompact = false,
    septemberPromo2025Enabled,
}: {
    isIOSPromo?: boolean;
    isAndroidPromo?: boolean;
    isMobilePayAvailable?: boolean;
    mayPromo2025Enabled?: boolean;
    isCompact?: boolean;
    septemberPromo2025Enabled?: boolean;
}) {
    const dispatch = useDispatch();

    const { daysLeft, isPromoActive, daysLeftText, periodLeftText } = usePromoCountdown(promotionEndDate);

    const { isLoading, isLoaded } = useSelector(ProductsSelectors.getLifeCycleState);
    const isMobile = useSelector(QuotaLandingSelectors.isMobile);
    const isWebview = IS_WEBVIEW || IS_WEBVIEW_FROM_UA;

    const productId = mayPromo2025YearProductId;

    useEffect(() => {
        if (mayPromo2025Enabled && productId) {
            sendPaymentGa({
                eventCategory: ECategoryGa.button,
                action: 'show',
                name: 'mnz_promo_action_ms_june25_landing',
            });
            emitAnalyticEvent(AnalyticEventNames.MAY_PROMO_2025_BUY_TARIFF_SHOW);
        }
    }, []);

    const onBuyClick = useCallback(() => {
        if (productId) {
            if ((isAndroidPromo || isIOSPromo) && !isMobilePayAvailable) {
                revealPlatformPopup(productId, isIOSPromo, isAndroidPromo);
                return;
            }

            sendPaymentGa({
                eventCategory: ECategoryGa.button,
                action: 'click',
                paySource: 'action_button1tb',
                name: 'mnz_promo_action_ms_june25_landing',
            });
            emitAnalyticEvent(AnalyticEventNames.MAY_PROMO_2025_BUY_TARIFF_CLICK);
            sendTmrGoal({ id: TMR3473039, goal: 'click-trial-button' });

            dispatch(
                openTariffBuy({
                    productId,
                    checkAnonymous: true,
                    isMobile,
                    paySource: getPaySource('action_button1tb'),
                    hideOtherTariffs: true,
                })
            );
        }
    }, [dispatch, isAndroidPromo, isIOSPromo, isMobile, isMobilePayAvailable, productId]);

    if (!isPromoActive || (!mayPromo2025Enabled && !septemberPromo2025Enabled)) {
        return null;
    }

    return (
        <div
            className={classNames(styles.root, {
                [styles.rootMobile]: isMobile,
                [styles.rootMayPromo2025]: mayPromo2025Enabled,
                [styles.rootCompact]: isCompact,
                [styles.rootSeptemberPromo2025]: septemberPromo2025Enabled,
                [styles.rootSeptemberPromo2025Mobile]: septemberPromo2025Enabled && isMobile && !isWebview,
                [styles.rootSeptemberPromo2025Webview]: septemberPromo2025Enabled && isWebview,
            })}
        >
            <div className={styles.contentWrapper}>
                <div className={styles.textWrapper}>
                    {daysLeft === 0 ? (
                        <>
                            Акция закончится{isMobile && <>&nbsp;</>}
                            <div
                                className={classNames({
                                    [styles.root_days]: !septemberPromo2025Enabled,
                                    [styles.period]: septemberPromo2025Enabled,
                                })}
                            >
                                сегодня
                            </div>
                        </>
                    ) : isCompact ? (
                        <>
                            {'Ещё'}
                            {isMobile && <>&nbsp;</>}
                            <div className={styles.root_days}>{daysLeftText}</div>
                            {isMobile && <>&nbsp;</>}
                            {'акции'}
                        </>
                    ) : septemberPromo2025Enabled ? (
                        <>
                            {'Повышение цены через'}
                            <div className={styles.period}>{periodLeftText}</div>
                        </>
                    ) : (
                        <>
                            {'Акция закончится через'}
                            {isMobile && <>&nbsp;</>}
                            <div className={styles.root_days}>{daysLeftText}</div>
                        </>
                    )}
                </div>
                {mayPromo2025Enabled && productId && (
                    <Button className={styles.buyTariffButton} primary theme="vk" onClick={onBuyClick} disabled={isLoading || !isLoaded}>
                        {isCompact ? '1 ТБ за 0 ₽' : '1 ТБ на 3 месяца бесплатно'}
                    </Button>
                )}
            </div>
        </div>
    );
}
